/**
 * engineNormalJson_Merge_Slot.ts - Slot component merging for EngineNormalJson
 * Pure ES Module version
 */

import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { getKeysList, stringifyValue } from './engineNormalJson_Helper.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { EngineNormalJson } from './engineNormalJson.js';
import {
    TYPE_HTML,
    TYPE_SLOTHTML,
    TYPE_PLACEHOLDER,
    TYPE_PLACEHOLDER_SKIP,
    TYPE_SLOT,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    META_KEY,
    isTypePrefixedKey
} from './engineNormalJson_Merge_Constants.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';

import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
export function convertToString(value: unknown): string {
    return stringifyValue(value);
}

/**
 * Merges a slot component with slot content
 * Similar to EnginePreProcessJson.MergeSlotComponent
 */
export function mergeSlotComponent(
    engine: EngineNormalJson,
    slotHtmlJson: JsonObject,
    slotholderData: JsonObject,
    appSite: string,
    slotName: string,
    appView: string | null,
    slotKey: string,
    operations: TemplateOperation[],
    jsonData: JsonObject,
    slotContentSourceTemplate: string,
    componentHtml: JsonObject | null,
    flowStack: string[] | null,
    genComponentHtml: boolean,
    contextJson: JsonObject | null,
    configJson: JsonObject | null = null,
    recursionDepth: number,
    clientMode: boolean = false
): string {
    let result = '';

    // Canonical phase ordering (matches Normal HTML engine): SlotMerge operations
    // emit in slotholder fill order (the order slots are defined in the holder block),
    // NOT the component template's placeholder scan order. HTML is still built in
    // component scan order below; only operation tracking is reordered. Component
    // placeholder / leftover key ops follow the slot merges, mirroring Normal's
    // "fill slots, then process placeholders" sequence.
    const slotMergeOps = new Map<string, TemplateOperation>();
    const tailOps: TemplateOperation[] = [];

    // Process keys in insertion order
    const keys = getKeysList([...slotHtmlJson.keys()]);

    for (const key of keys) {
        // Skip metadata
        if (key === META_KEY) continue;

        const value: JsonValue = slotHtmlJson.get(key);

        // Get type from first character for type-prefixed keys
        const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';
        const keyNameFromKey = GenerateHtmlJson.getKeyName(key);

        if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
            // Regular HTML content
            result += value.asString() ?? '';
        } else if (keyType === TYPE_PLACEHOLDER || keyType === TYPE_PLACEHOLDER_SKIP) {
            // Placeholder inside slot - recursively resolve
            // Use slotContentSourceTemplate for reuse pattern detection
            const valueStr = value.asString() ?? '';
            const componentName = keyNameFromKey || (valueStr ? valueStr.substring(2, valueStr.length - 2) : '');

            // Create immutable copy of flow stack with new item
            const newFlowStack = [...(flowStack || []), componentName];

            Logger.flow(`MergeSlotComponent: slot=${slotName} placeholder=${componentName} sourceTemplate=${slotContentSourceTemplate}`, "NormalJson.SlotExpansion");
            const startGetSlotComp = performance.now();
            const { htmlJson: componentHtmlJson, dataJson: componentDataJson, resolvedTemplateName, resolvedAppSite: resolvedComponentAppSite } = engine._loader!.getTemplateWithView(appSite, slotContentSourceTemplate, componentName, appView, newFlowStack, configJson, clientMode);
            engine._getTemplateNanos += EngineNormalJson.msToNanos(performance.now() - startGetSlotComp);
            engine._getTemplateCount++;
            Logger.flow(`MergeSlotComponent: slot=${slotName} placeholder=${componentName} resolved=${resolvedTemplateName == null ? 'null' : resolvedTemplateName} resolvedSize=${componentHtmlJson ? componentHtmlJson.size : 0}`, "NormalJson.SlotExpansion");

            const slotViewHtmlJson = componentHtmlJson;

            if (slotViewHtmlJson) {
                const childOperations: TemplateOperation[] = [];
                const mergeJsonData: JsonObject = componentDataJson ? new JsonObject(componentDataJson) : jsonData;
                const componentKey = `${resolvedComponentAppSite}_${resolvedTemplateName || componentName}`;

                // Check for skip-marked placeholders and missing templates

                // Pre-order: Set empty entry BEFORE recursion if genComponentHtml is enabled
                // Use componentName (placeholder name) for consistency with C# EngineNormal
                if (genComponentHtml && componentHtml) {
                    componentHtml.set(componentName, '');
                }

                const merged = engine.mergeTemplate(
                    slotViewHtmlJson,
                    appSite,
                    resolvedTemplateName || componentName,  // Use resolved template name, not placeholder name
                    appView,
                    componentKey,
                    childOperations,
                    mergeJsonData,
                    componentHtml,
                    newFlowStack,
                    genComponentHtml,
                    contextJson,
                    configJson,
                    recursionDepth + 1,
                    null,
                    null,
                    null,
                    null,
                    clientMode
                );
                result += merged;

                // Post-order: Update entry AFTER recursion if genComponentHtml is enabled
                // Use componentName (placeholder name) for consistency with C# EngineNormal
                if (genComponentHtml && componentHtml) {
                    componentHtml.set(componentName, merged);
                }

                tailOps.push({
                    templateName: slotKey,
                    kind: TemplateOperationKind.ComponentReplace,
                    target: `{{${componentName}}}`,
                    replacedWith: componentKey,
                    operations: childOperations
                } as TemplateOperation);
            } else {
                result += valueStr;
            }

        } else if (keyType === TYPE_SLOT) {
            // Slot injection point - replace with slot content
            // In new format: i3_HTMLPLACEHOLDER, extract name from key
            const valueStr = value.asString() ?? '';
            const slotContentKey = keyNameFromKey || (valueStr ? valueStr.slice(3, -2) : ''); // Extract "HTMLPLACEHOLDER"

            if (slotholderData.has(slotContentKey)) {
                const slotContentValue = slotholderData.get(slotContentKey);
                const slotContent = slotContentValue.asObject();

                const slotContentOperations: TemplateOperation[] = [];

                if (slotContent) {
                    // Merge slot content with proper operations tracking
                    // Use slotContentSourceTemplate (not slotName) since slot content comes from parent template
                    const merged = engine.mergeTemplate(
                        slotContent,
                        appSite,
                        slotContentSourceTemplate,
                        appView,
                        slotKey,
                        slotContentOperations,
                        jsonData,
                        componentHtml,
                        flowStack,
                        genComponentHtml,
                        contextJson,
                        configJson,
                        recursionDepth + 1,
                        null,
                        null,
                        null,
                        null,
                        clientMode
                    );

                    // NOTE: Slot content view handlers removed - synthetic names (HTMLPLACEHOLDER) don't have handlers in dynamicview.json
                    // Components inside slot content get their handlers applied via their own GetTemplate calls

                    result += merged;

                    // Add operation tracking with nested operations from slot content
                    slotMergeOps.set(slotContentKey, {
                        templateName: slotKey,
                        kind: TemplateOperationKind.SlotMerge,
                        target: valueStr,
                        replacedWith: slotContentKey,
                        operations: slotContentOperations
                    } as TemplateOperation);
                } else {
                    // String slot content - use directly
                    result += slotContentValue.asString() ?? '';
                }
            } else {
                // Slot content not found - keep as-is
                result += valueStr;
            }
        } else if (keyType === TYPE_JSONKEY || keyType === TYPE_JSONKEY_SKIP) {
            // JSON key inside a slot component. These can be slot content injections like {{$HTMLPLACEHOLDER1}}.
            const keyNameForLookup = keyNameFromKey;
            const valueStr = value.asString() ?? '';

            // If the slotholder provided content for this key, merge that content instead of looking in jsonData
            // Skip SlotHtml entries (whitespace between markers) - they are strings, not Dictionaries
            if (slotholderData.has(keyNameForLookup)) {
                const slotContentValue = slotholderData.get(keyNameForLookup);
                const slotContent = slotContentValue.asObject();

                if (slotContent) {
                    const slotContentOperations: TemplateOperation[] = [];

                    // Use slotContentSourceTemplate (not slotName) since slot content comes from parent template
                    const merged = engine.mergeTemplate(
                        slotContent,
                        appSite,
                        slotContentSourceTemplate,
                        appView,
                        slotKey,
                        slotContentOperations,
                        jsonData,
                        componentHtml,
                        flowStack,
                        genComponentHtml,
                        contextJson,
                        configJson,
                        recursionDepth + 1,
                        null,
                        null,
                        null,
                        null,
                        clientMode
                    );

                    // NOTE: Slot content view handlers removed - synthetic names (HTMLPLACEHOLDER) don't have handlers in dynamicview.json
                    // Components inside slot content get their handlers applied via their own GetTemplate calls

                    result += merged;

                    slotMergeOps.set(keyNameForLookup, {
                        templateName: slotKey,
                        kind: TemplateOperationKind.SlotMerge,
                        target: valueStr,
                        replacedWith: keyNameForLookup,
                        operations: slotContentOperations
                    } as TemplateOperation);
                } else {
                    // It's a string value from slotholder - use it directly
                    result += slotContentValue.asString() ?? '';
                }
            } else if (jsonData.has(keyNameForLookup)) {
                const replacedValue = convertToString(jsonData.get(keyNameForLookup));
                result += replacedValue;

                tailOps.push({
                    templateName: slotKey,
                    kind: TemplateOperationKind.KeyReplace,
                    target: valueStr,
                    replacedWith: `${slotName}_${keyNameForLookup}`,
                    operations: [] as TemplateOperation[]
                } as TemplateOperation);
            } else {
                // Use the already-merged value from the key (context priority was applied during merge)
                result += convertToString(value);
            }
        } else {
            // Nested object - recurse
            const nestedObj = value.asObject();
            if (nestedObj) {
                const nested = mergeSlotComponent(engine, nestedObj, slotholderData, appSite, slotName, appView, slotKey, operations, jsonData, slotContentSourceTemplate, componentHtml, flowStack, genComponentHtml, contextJson, configJson, recursionDepth + 1, clientMode);
                result += nested;
            }
        }
    }

    // Emit SlotMerge operations in slotholder fill order (holder block definition
    // order), matching Normal's ExtractSlotContents traversal. Then append the
    // component placeholder / leftover key operations.
    for (const slotholderKey of slotholderData.keys()) {
        const slotMergeOp = slotMergeOps.get(slotholderKey);
        if (slotMergeOp) {
            operations.push(slotMergeOp);
        }
    }
    operations.push(...tailOps);

    return result;
}
